/** Tagesabschluss: Abgleich der Pakete am Abholplatz, Bestätigen → übergeben (Prozessdesign 04 §5). */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tagesabschluss, tagesabschlussBestaetigen } from '../api/lager';
import { versandproduktText } from '@storeos/contracts';
import { datum, datumZeit, gewicht, uhrzeit } from '../components/format';
import { Feld, Karte, Kachel, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';

export function TagesabschlussSeite() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['tagesabschluss'], queryFn: tagesabschluss });
  const [gezaehlt, setGezaehlt] = useState('');
  const [trotzdem, setTrotzdem] = useState(false);
  const m = useMutation({
    mutationFn: (v: { n: number; force: boolean }) => tagesabschlussBestaetigen(v.n, v.force),
    onSuccess: (r) => {
      toast(`${r.anzahl} Sendungen übergeben. Versandmails und Woo-Status laufen über die Outbox.${r.hinweise.length ? ` ${r.hinweise.join(' · ')}` : ''}`, r.uebersprungen.length ? 'fehler' : undefined);
      setGezaehlt(''); setTrotzdem(false); void qc.invalidateQueries();
    },
    onError: (e) => toast((e as Error).message, 'fehler'),
  });
  const d = q.data;
  const anzahl = d?.sendungen.length ?? 0;
  const n = gezaehlt === '' ? null : Number(gezaehlt);
  const abweichung = n !== null && n !== anzahl;
  const vomVortag = (x: string | null) => Boolean(x && x.slice(0, 10) < (d?.datum ?? ''));
  const aelter = d?.sendungen.filter((s) => vomVortag(s.label_at)).length ?? 0;

  return (
    <section>
      <Seitenkopf titel="Tagesabschluss" unter={d ? `Stand ${datum(d.datum)} · alle Sendungen mit Label, die noch nicht übergeben sind · tägliche DHL-Abholung ${d.abholung ? `um ${d.abholung}` : '(Uhrzeit offen)'} · Cut-off ${d.cutoff ?? '14:00'} Uhr` : undefined}>
        <button type="button" className="knopf-zweit" onClick={() => window.print()} disabled={!anzahl}>Abholliste drucken</button>
      </Seitenkopf>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {d && (
          <>
            <div className="kacheln klein">
              <Kachel wert={<span data-testid="ta-anzahl">{anzahl}</span>} text="Pakete im System" klasse="k-label_erstellt" />
              {Object.entries(d.je_produkt).map(([produkt, n]) => <Kachel key={produkt} wert={n} text={versandproduktText(produkt)} />)}
              {d.nicht_bestaetigt > 0 && <Kachel wert={d.nicht_bestaetigt} text="ohne Label-Scan" klasse="k-klaerfall" />}
              {aelter > 0 && <Kachel wert={aelter} text="Labels von Vortagen" klasse="k-klaerfall" />}
            </div>
            {d.storno_alarme.length > 0 && (
              <Karte titel={`Storno: ${d.storno_alarme.length} Paket(e) NICHT mitgeben`} klasse="alarm nicht-drucken">
                <ul className="liste-einfach storno-liste" data-testid="ta-storno">
                  {d.storno_alarme.map((x) => (
                    <li key={x.sendung_id}><Link to={`/auftraege/${x.order_id}`} className="mono">{x.kennung}</Link>
                      <span>{x.label_storniert === true ? 'Label storniert' : x.label_storniert === false ? 'Label NICHT storniert' : 'ohne Label'}</span>
                      <span className="hinweis">{(x.text ?? '').replace(/^STORNO:\s*/, '')}</span></li>
                  ))}
                </ul>
                <p className="hinweis">Paket vom Abholplatz nehmen, Ware zurück ins Fach, dann an der Packstation die Kennung scannen und Enter drücken.</p>
              </Karte>
            )}
            <Karte titel="Abgleich am Abholplatz" klasse="nicht-drucken">
              <form className="abgleich" onSubmit={(e) => { e.preventDefault(); if (!anzahl) return; if (abweichung && !trotzdem) return; m.mutate({ n: n ?? anzahl, force: abweichung && trotzdem }); }}>
                <Feld label="Gezählte Pakete am Abholplatz">
                  <input type="number" min={0} value={gezaehlt} onChange={(e) => { setGezaehlt(e.target.value); setTrotzdem(false); }} data-testid="ta-gezaehlt" inputMode="numeric" />
                </Feld>
                {abweichung && (
                  <div className="warn-box">
                    <strong>Abweichung: {n} gezählt, {anzahl} im System.</strong> Prüfe die Liste unten (Tracking-Nummern am Paket) und lege fehlende Pakete dazu.
                    <label className="inline"><input type="checkbox" checked={trotzdem} onChange={(e) => setTrotzdem(e.target.checked)} /> Trotzdem alle {anzahl} übergeben</label>
                  </div>
                )}
                <button type="submit" className="knopf gross" disabled={!anzahl || m.isPending || (abweichung && !trotzdem)} data-testid="ta-bestaetigen">
                  {m.isPending ? 'Übergebe …' : `${anzahl} Sendungen übergeben`}
                </button>
                <p className="hinweis">Bestätigen setzt alle auf „Übergeben“, bucht den Bestandsabgang, schickt die Versandmails und meldet „completed“ an Woo (bei ausgeschaltetem Schalter als Trockenlauf im <Link to="/protokoll?tab=outbox">Protokoll</Link>).</p>
              </form>
            </Karte>
            <Karte titel="Abholliste" klasse="druckbar">
              <p className="nur-druck">Abholliste {datum(d.datum)} · {anzahl} Pakete · Nuts &amp; Golden</p>
              <Tabelle testid="ta-liste" zeilen={d.sendungen} schluessel={(z) => z.sendung_id} leer="Keine offenen Labels. Nichts zu übergeben."
                spalten={[
                  { kopf: 'Sendung', zelle: (z) => <Link to={`/auftraege/${z.order_id}`} className="mono">{z.kennung}</Link> },
                  { kopf: 'Empfänger', zelle: (z) => `${z.kunde_name ?? '–'} · ${z.lieferland}` },
                  { kopf: 'Produkt', zelle: (z) => versandproduktText(z.versandprodukt) },
                  { kopf: 'Tracking', zelle: (z) => z.tracking_nummer ?? '–', klasse: 'mono' },
                  { kopf: 'Gewicht', zelle: (z) => gewicht(z.gewicht_g), rechts: true },
                  { kopf: 'Label', zelle: (z) => (vomVortag(z.label_at) ? <span className="warn-text">{datumZeit(z.label_at)}</span> : uhrzeit(z.label_at)), rechts: true },
                  { kopf: 'Scan', zelle: (z) => (z.label_bestaetigt ? '✓' : <span className="warn-text">offen</span>), rechts: true },
                ]} />
            </Karte>
            {d.verlauf.length > 0 && (
              <Karte titel="Letzte Abschlüsse" klasse="nicht-drucken">
                <ul className="liste-einfach" data-testid="ta-verlauf">{d.verlauf.map((x) => <li key={x.id}><span>{datumZeit(x.created_at)}</span><span>{x.anzahl} Pakete{x.erwartet_anzahl !== null && x.erwartet_anzahl !== x.anzahl ? ` (gezählt ${x.erwartet_anzahl})` : ''}</span><span className="hinweis">{x.modus === 'auto' ? 'automatisch' : x.benutzer ?? '–'}</span></li>)}</ul>
              </Karte>
            )}
          </>
        )}
      </Zustand>
    </section>
  );
}
